import { ChangeEvent, DragEvent, RefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type UseDialogUploadFileParams = Readonly<{
  acceptedExtensions: string[];
  buttonTextSuccess?: string;
  isMultiple: boolean;
  isOpen: boolean;
  maxSizeBytes: number;
  onSuccess?: (files: File[]) => void;
  title?: string;
}>;

type UseDialogUploadFileResult = Readonly<{
  acceptAttribute: string;
  allowedFormatsLabel: string;
  allowedFormatsText: string;
  browseText: string;
  buttonTextSuccessText: string;
  dragAnnouncementText: string;
  dropTitleText: string;
  errorText: string;
  fileInputRef: RefObject<HTMLInputElement>;
  formatFileSize: (bytes: number) => string;
  getDeleteFileLabel: (fileName: string) => string;
  getFileIconType: (fileName: string) => string;
  handleClickSelect: () => void;
  handleDragEnter: (event: DragEvent) => void;
  handleDragLeave: (event: DragEvent) => void;
  handleDragOver: (event: DragEvent) => void;
  handleDrop: (event: DragEvent) => void;
  handleFileInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSuccess: () => void;
  hasFiles: boolean;
  isDragOver: boolean;
  makeHandleRemoveFile: (index: number) => () => void;
  maxFileSizeLabel: string;
  maxFileSizeText: string;
  rejectedFiles: string[];
  selectedFiles: File[];
  selectedFilesLabel: string;
  titleText: string;
  uploadFileAriaLabel: string;
  uploadingAriaLabel: string;
}>;

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function getFileIconType(fileName: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'pdf') return 'file-pdf';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'file-image';
  if (['mp4', 'webm', 'mov', 'ogv'].includes(ext)) return 'file-video';
  if (['mp3', 'wav', 'ogg', 'aac'].includes(ext)) return 'file-audio';
  if (['doc', 'docx'].includes(ext)) return 'file-doc';
  return 'file-document';
}

function useDialogUploadFile({
  acceptedExtensions,
  buttonTextSuccess,
  isMultiple,
  isOpen,
  maxSizeBytes,
  onSuccess,
  title,
}: UseDialogUploadFileParams): UseDialogUploadFileResult {
  const { t } = useTranslation(['actions', 'common', 'content']);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [rejectedFiles, setRejectedFiles] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef<number>(0);

  const normalizedExtensions = acceptedExtensions.map((ext) => ext.toLowerCase());
  const acceptAttribute = normalizedExtensions.map((ext) => `.${ext}`).join(',');
  const allowedFormatsLabel = normalizedExtensions.map((ext) => ext.toUpperCase()).join(', ');

  useEffect(() => {
    if (!isOpen) {
      setSelectedFiles([]);
      setIsDragOver(false);
      setRejectedFiles([]);
      dragCounterRef.current = 0;
    }
  }, [isOpen]);

  function isValidFile(file: File): boolean {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    return file.size <= maxSizeBytes && normalizedExtensions.includes(ext);
  }

  function addFiles(files: File[]): void {
    const valid: File[] = [];
    const rejectedNames: string[] = [];
    files.forEach((file) => {
      if (isValidFile(file)) valid.push(file);
      else rejectedNames.push(file.name);
    });

    if (valid.length > 0) {
      if (isMultiple) {
        setSelectedFiles((prev) => [...prev, ...valid]);
      } else {
        setSelectedFiles([valid[valid.length - 1]]);
      }
    }
    setRejectedFiles(rejectedNames);
  }

  function handleDragEnter(event: DragEvent): void {
    event.preventDefault();
    dragCounterRef.current += 1;
    setIsDragOver(true);
  }

  function handleDragLeave(event: DragEvent): void {
    event.preventDefault();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setIsDragOver(false);
  }

  function handleDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  function handleDrop(event: DragEvent): void {
    event.preventDefault();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) addFiles(files);
  }

  function handleClickSelect(): void {
    fileInputRef.current?.click();
  }

  function handleFileInputChange(event: ChangeEvent<HTMLInputElement>): void {
    const files = Array.from(event.target.files ?? []);
    if (files.length > 0) addFiles(files);
    event.target.value = '';
  }

  function makeHandleRemoveFile(index: number): () => void {
    return function handleRemoveFileAt(): void {
      setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    };
  }

  function handleSuccess(): void {
    onSuccess?.(selectedFiles);
  }

  function getDeleteFileLabel(fileName: string): string {
    return t('actions:Delete file', { name: fileName });
  }

  const maxFileSizeLabel = formatFileSize(maxSizeBytes);
  const titleText = title ?? t('common:Upload file');
  const buttonTextSuccessText = buttonTextSuccess ?? t('actions:Add');
  const dropTitleText = t('content:Drop your file here or');
  const browseText = t('content:Browse');
  const maxFileSizeText = t('actions:Max file size');
  const allowedFormatsText = t('actions:Allowed formats');
  const errorText = t('content:Some files could not be added');
  const selectedFilesLabel = t('common:Selected files');
  const uploadFileAriaLabel = t('common:Upload file');
  const dragAnnouncementText = t('content:Drop file to upload');
  const uploadingAriaLabel = t('common:Uploading file');

  return {
    acceptAttribute,
    allowedFormatsLabel,
    allowedFormatsText,
    browseText,
    buttonTextSuccessText,
    dragAnnouncementText,
    dropTitleText,
    errorText,
    fileInputRef,
    formatFileSize,
    getDeleteFileLabel,
    getFileIconType,
    handleClickSelect,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleFileInputChange,
    handleSuccess,
    hasFiles: selectedFiles.length > 0,
    isDragOver,
    makeHandleRemoveFile,
    maxFileSizeLabel,
    maxFileSizeText,
    rejectedFiles,
    selectedFiles,
    selectedFilesLabel,
    titleText,
    uploadFileAriaLabel,
    uploadingAriaLabel,
  };
}

export default useDialogUploadFile;
